import { useEffect, useRef } from 'react';

/**
 * <dialog> 기반 모달.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose  ESC·백드롭·닫기 버튼 어느 경로로 닫혀도 호출된다
 * @param {{current: HTMLElement|null}} [props.initialFocusRef]
 *   showModal() 은 기본적으로 첫 포커스 가능 요소(닫기 X)에 포커스를 넣는다.
 *   제목부터 읽히게 하려면 여기에 tabindex=-1 을 준 제목 ref 를 넘긴다.
 * @param {{current: HTMLElement|null}} [props.returnFocusRef]
 *   닫힌 뒤 포커스를 돌려줄 곳. 여는 트리거가 닫히면서 사라지는 경우
 *   (팝오버 안의 버튼 등) 브라우저 기본 복원이 통하지 않아 필요하다.
 */
export function Modal({
  open,
  onClose,
  initialFocusRef,
  returnFocusRef,
  children,
  ...rest
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || typeof dialog.showModal !== 'function') return;

    if (open && !dialog.open) {
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      initialFocusRef?.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, initialFocusRef]);

  // 열려 있는 채로 언마운트되면 스크롤 잠금이 남는다
  useEffect(
    () => () => {
      document.body.style.overflow = '';
    },
    [],
  );

  const handleClose = () => {
    document.body.style.overflow = '';
    returnFocusRef?.current?.focus();
    onClose?.();
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      onClick={(event) => {
        // 백드롭 클릭 — dialog 자신이 타깃이면 내용 밖이다
        if (event.target === dialogRef.current) dialogRef.current.close();
      }}
      {...rest}
    >
      {children}
    </dialog>
  );
}
